import * as E from "fp-ts/Either";
import { pipe } from "fp-ts/lib/function";
import * as t from "io-ts";
import { formatValidationErrors } from "io-ts-reporters";
import React from "react";
import RestClient from "../../lib/RestClient";

interface UseLocationAppointmentsArgs {
  locationId: number;
  scanTypeId: string;
  bodyPartIds: number[];
  cursor?: string;
}

export const AvailabilitySlot = t.type({
  ids: t.array(t.number),
  start_time: t.string,
  end_time: t.string,
  next_day_slot: t.boolean,
});

export type AvailabilitySlot = t.TypeOf<typeof AvailabilitySlot>;

const AvailabilitySlotsResponse = t.type({
  date_range: t.type({
    start: t.string,
    end: t.string,
    has_previous: t.boolean,
  }),
  availability_slots: t.array(AvailabilitySlot),
});

type Pagination = { from?: string; before?: string };

export type AvailabilitySlotsResponse = t.TypeOf<
  typeof AvailabilitySlotsResponse
>;

export const useAvailabilitySlots = ({
  locationId,
  scanTypeId,
  bodyPartIds,
  cursor = "",
}: UseLocationAppointmentsArgs) => {
  const [data, setData] = React.useState<AvailabilitySlotsResponse | null>(
    null
  );

  const [loading, setLoading] = React.useState(false);
  const [refetchToken, setRefetchToken] = React.useState(0);
  const [pagination, setPagination] = React.useState<Pagination>({
    from: cursor,
  });

  const fetchAppointments = async () => {
    const url = new URL(
      `/api/v1/scanning_locations/${locationId}/availability_slots`,
      window.location.origin
    );
    url.searchParams.set("scan_type_id", scanTypeId);
    bodyPartIds.forEach((bodyPartId) =>
      url.searchParams.append("body_part_id[]", `${bodyPartId}`)
    );

    if (pagination.from) {
      url.searchParams.set("from", pagination.from);
    }

    if (pagination.before) {
      url.searchParams.set("before", pagination.before);
    }

    setLoading(true);

    try {
      pipe(
        await RestClient.get(url.toString()),
        (response) => response.data,
        AvailabilitySlotsResponse.decode,
        E.mapLeft(formatValidationErrors),
        E.fold(
          (errors) => console.error("Unexpected response", errors),
          (data) => setData(data)
        )
      );
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchAppointments();
  }, [locationId, scanTypeId, pagination, bodyPartIds.join(","), refetchToken]);

  const hasPrevPage = data?.date_range.has_previous;

  return {
    slots: data?.availability_slots,
    dateRange: data?.date_range,
    loading,
    setPagination,
    cursor,
    hasPrevPage,
    refetch: () => setRefetchToken((token) => token + 1),
  };
};
